/**
 * Auth Service
 *
 * Authentication API methods.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

export interface User {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role: string;
  tenantId?: string;
  emailVerified: boolean;
  imageUrl?: string;
}

export interface VerifyResponse {
  valid: boolean;
  userId: string;
  expiresAt: string;
}

/**
 * Portal identifier — the user-facing application this membership grants access to.
 * Kept deliberately small. If business splits into more portals later, extend here
 * and in the backend's `AuthService.getUserPortals`.
 */
export type Portal = 'journey' | 'business';

/**
 * A single tenant membership plus the portal(s) it unlocks for the user.
 * Mirrors `user_tenants` row, enriched with tenant-level metadata.
 */
export interface PortalMembership {
  portal: Portal;
  hasAccess: boolean;
  tenantId: string;
  tenantName: string;
  tenantType: 'b2c' | 'b2b' | 'b2b2c' | 'b2g';
  role: string;
  isDefault: boolean;
}

export interface PortalsResponse {
  identity: {
    userId: string;
    email: string;
  };
  portals: PortalMembership[];
}

// Password reset types and methods removed — Clerk handles password
// reset natively via the reset_password_email_code strategy.

export class AuthService {
  constructor(private readonly client: TonnieClient) {}

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return this.client.get<User>(API_ENDPOINTS.AUTH.ME);
  }

  /**
   * Get the portal memberships for the current user.
   *
   * Used by portal apps to decide whether the signed-in identity has access
   * to *this* portal. Frontend branches on the returned list to render the
   * portal UI, a "no access" screen, or an invitation-accept flow.
   */
  async getMyPortals(): Promise<PortalsResponse> {
    return this.client.get<PortalsResponse>(API_ENDPOINTS.AUTH.ME_PORTALS);
  }

  /**
   * Verify a session token
   */
  async verifyToken(token: string): Promise<VerifyResponse> {
    return this.client.post<VerifyResponse>(API_ENDPOINTS.AUTH.VERIFY, { token });
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    return this.client.post<void>(API_ENDPOINTS.AUTH.LOGOUT);
  }

  // Password reset methods removed — handled by Clerk natively.
}

export function createAuthService(client: TonnieClient): AuthService {
  return new AuthService(client);
}
